import { Icon } from '@iconify/react';
import { useState, useMemo } from 'react';
import { ArticleType, Message, useGetExpertAnswerByMessageIdMutation, RootState } from '@circuitry-ai/doc-data';
import { ArticleModel, convertToLocalDayMonthYear, CtyButton, getUserName, useHasApplicationResource } from '@circuitry-ai/doc-ui';
import { Spinner } from '@nextui-org/react';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';

export enum feedbackOptions {
    EXPERT_ANSWER = 'Expert Answer',
}

export enum chatOptionsDialogType {
    COMMENT = 'Comment',
    TRANSLATE = 'Translation Options',
    EXPERT_ANSWER = 'Expert Answer',
}

interface ExpertAnswerProps {
    message: Partial<Message>,
    advisor_id: string,
    sender_id: string,
}

export const ExpertAnswer: React.FC<ExpertAnswerProps> = ({ message, advisor_id, sender_id }) => {
    const { details: user, isAuthenticated } = useSelector((state: RootState) => state.auth.user);
    const userName = getUserName(isAuthenticated, user);
    const hasExpertAnswerAccess = useHasApplicationResource('expert_answer');

    const [expertAnswerModalData, setExpertAnswerModalData] = useState<any>(null);

    const calculdateMetadata = () => {
        let baseMap: Record<string, any> = {};

        if (Array.isArray(message.metaData)) {
            baseMap = message.metaData.reduce((acc: any, obj: any) => {
                const [key, value] = Object.entries(obj)[0] || [];
                if (key) acc[key] = value;
                return acc;
            }, {});
        } else if (message.metaData && typeof message.metaData === 'object') {
            baseMap = { ...message.metaData };
        }
        return baseMap;
    }

    const metadataMap = useMemo(() => {
        return calculdateMetadata();
    }, [message?.metaData]);

    const [getExpertAnswerByMessageId, { isLoading: isExpertAnswerLoading }] = useGetExpertAnswerByMessageIdMutation();
    const message_id=message?.metaData?.find((item:any)=>item?.message_id)?.message_id ?? ''

    const resolvedSenderId = sender_id || metadataMap?.session_id || message?.metaData?.[2]?.session_id || '';
    const resolvedMessageId = metadataMap?.message_id || message_id || '';

    const handleFetchExpertAnswer = async () => {
        try {
            const response = await getExpertAnswerByMessageId({ sender_id: resolvedSenderId, message_id: resolvedMessageId }).unwrap();
            const articleData = Array.isArray(response) ? response[0] : response?.data?.[0];

            if (articleData) {
                setExpertAnswerModalData({
                    question: articleData?.question,
                    answer: articleData?.answer,
                    name: '',
                    metadata: articleData?.metadata?.length > 0 ? articleData?.metadata : [{ name: '', value: '', id: uuidv4() }],
                    article_type: articleData?.type,
                    article_status: articleData?.status,
                    advisor_id: articleData?.advisor_id || advisor_id || '',
                    author: articleData?.created_by,
                    upload_date: convertToLocalDayMonthYear(articleData?.created_at).split(',')[0],
                    article_id: articleData?.article_id,
                    sender_id: articleData?.sender_id || resolvedSenderId,
                    message_id: resolvedMessageId,
                })
            } else {
                setExpertAnswerModalData({
                    question: metadataMap?.rephrased_query ?? '',
                    answer: message?.message ?? '',
                    name: '',
                    metadata: [{ name: '', value: '', id: uuidv4() }],
                    article_type: ArticleType.EXPERT_ANSWER,
                    article_status: '',
                    advisor_id: advisor_id ?? '',
                    author: userName ?? '',
                    upload_date: convertToLocalDayMonthYear(new Date().toISOString()).split(',')[0],
                    article_id: '',
                    sender_id: resolvedSenderId,
                    message_id: resolvedMessageId,
                });
            }

        } catch (error) {
            console.log(error);
        }
    }


    if ((message?.metaData ?? [])?.length === 0) {
        return null;
    }

    return (
        <>
            {hasExpertAnswerAccess && (<CtyButton
                tooltipContent={feedbackOptions.EXPERT_ANSWER}
                toolTipColor='warning'
                id={`expertanswer-btn-${message?.isAdvisor && message?.metaData?.length ? message?.metaData?.[6]?.message_id : 'greet'}`}
                isIconOnly
                radius="full"
                size="sm"
                variant="light"
                onPress={handleFetchExpertAnswer}
                isDisabled={!hasExpertAnswerAccess}
            >
                <div className="relative">
                    {isExpertAnswerLoading && <Spinner className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2" />}
                    <Icon icon="bi:patch-check" height={16} width={16} />
                </div>
            </CtyButton>
            )}
            {expertAnswerModalData && (
                <ArticleModel
                    onClose={() => setExpertAnswerModalData(null)}
                    articleData={expertAnswerModalData}
                />
            )}
        </>

    );
};
